class CanvasRecorderPerformanceStats {
    constructor() {
        this.missedOnWindow = 0;
        this.badGroups = 0;
    }
    reset() {
        if (this.missedOnWindow > CanvasRecorderPerformanceStats.lossOnWinddowAllowed) {
            this.badGroups++;
        }
        this.windowStartTs = undefined;
        this.missedOnWindow = 0;
    }
    tickMissed(timestamp) {
        var _a;
        if (!this.windowStartTs) {
            this.windowStartTs = timestamp;
            this.missedOnWindow = 1;
            return;
        }
        if (timestamp - this.windowStartTs > CanvasRecorderPerformanceStats.windowSizeMs) {
            if (this.missedOnWindow > CanvasRecorderPerformanceStats.lossOnWinddowAllowed) {
                this.badGroups++;
                (_a = this.onBadGroup) === null || _a === void 0 ? void 0 : _a.call(this);
            }
            this.windowStartTs = timestamp;
            this.missedOnWindow = 1;
        }
        else {
            this.missedOnWindow++;
        }
    }
    pause() {
        this.reset();
    }
    getStats() {
        var _a;
        const r = (_a = this.badGroups) !== null && _a !== void 0 ? _a : 0;
        this.reset();
        return r;
    }
}
CanvasRecorderPerformanceStats.windowSizeMs = 5000;
CanvasRecorderPerformanceStats.lossOnWinddowAllowed = 5;
class CanvasRecorder {
    constructor(options) {
        var _a, _b;
        this.video = null;
        this.decodedFramesCount = 0;
        this.timeoutHandle = null;
        this.animationFrameRequestHandle = null;
        this.stubFastdrawFrameCount = 0;
        this.frameUpdateCallback = this.frameUpdate.bind(this);
        this.cachedBlob = null;
        this.lastCbTimestamp = null;
        this.lowFpsMode = false;
        this.lowFpsTimeoutHandle = null;
        this.performanceStats = new CanvasRecorderPerformanceStats();
        this.ondataavailable = null;
        this.onerror = null;
        this.onpause = null;
        this.onresume = null;
        this.onstart = null;
        this.onstop = null;
        this.canvasMode = (_a = options.canvasMode) !== null && _a !== void 0 ? _a : false;
        const audioTrack = options.mediaStream.getAudioTracks()[0];
        if (!(audioTrack instanceof MediaStreamTrack) || audioTrack.readyState !== 'live') {
            throw new Error('audioTrack must be an active media stream track');
        }
        const videoTrack = options.mediaStream.getVideoTracks()[0];
        this.canvas = document.createElement('canvas');
        if (videoTrack) {
            const settings = videoTrack.getSettings();
            this.canvas.width = settings.width;
            this.canvas.height = settings.height;
        }
        else {
            this.canvas.width = 1280;
            this.canvas.height = 720;
        }
        this.ctx = this.canvas.getContext("2d", { alpha: false, desynchronized: true });
        this.ctx.fillStyle = "black";
        this.watermarkImg = options.watermark ? { img: options.watermark,
            rect: [0, 0, 0, 0] } : undefined;
        if (options.stub) {
            this.stubImg = { img: options.stub, rect: this.letterbox(options.stub) };
        }
        this.videoElement = this.canvasMode ? null : ((_b = options.video) !== null && _b !== void 0 ? _b : null);
        this.canvasTrack = this.canvas.captureStream().getVideoTracks()[0];
        const stream = this.canvasMode ? new MediaStream([this.canvasTrack, audioTrack]) : options.mediaStream;
        const container = 'video/webm';
        let videoCodec = 'vp8';
        let audioCodec = MediaRecorder.isTypeSupported('audio/webm;codecs=pcm') ? 'pcm' : 'opus';
        if (MediaRecorder.isTypeSupported(`${container};codecs=avc1.640034,${audioCodec}`)) {
            videoCodec = 'avc1.640034';
        }
        else if (MediaRecorder.isTypeSupported(`${container};codecs=h264,${audioCodec}`)) {
            videoCodec = 'h264';
        }
        this.mimeType = `${container};codecs=${videoCodec},${audioCodec}`;
        const mediaRecorderOpts = {
            videoBitsPerSecond: CanvasRecorder.getVideoBitrateForTrack(stream.getVideoTracks()[0]),
            mimeType: this.mimeType
        };
        console.log('Creating MediaRecorder with options', mediaRecorderOpts);
        this.mediaRecorder = new MediaRecorder(stream, mediaRecorderOpts);
        this.mediaRecorder.ondataavailable = CanvasRecorder.hasH264Support ? (e) => { var _a; return (_a = this.ondataavailable) === null || _a === void 0 ? void 0 : _a.call(this, e); } : this.blobResizeDecorator.bind(this);
        this.mediaRecorder.onerror = (e) => { var _a; return (_a = this.onerror) === null || _a === void 0 ? void 0 : _a.call(this, e); };
        this.mediaRecorder.onpause = (e) => { var _a; return (_a = this.onpause) === null || _a === void 0 ? void 0 : _a.call(this, e); };
        this.mediaRecorder.onresume = (e) => { var _a; return (_a = this.onresume) === null || _a === void 0 ? void 0 : _a.call(this, e); };
        this.mediaRecorder.onstart = (e) => { var _a; return (_a = this.onstart) === null || _a === void 0 ? void 0 : _a.call(this, e); };
        this.mediaRecorder.onstop = CanvasRecorder.hasH264Support ? (e) => { var _a; return (_a = this.onstop) === null || _a === void 0 ? void 0 : _a.call(this, e); } : this.onStopDecorator.bind(this);
        document.addEventListener("visibilitychange", () => {
            if (this.animationFrameRequestHandle !== null || this.timeoutHandle !== null) {
                this.setNextUpdateTimer();
            }
        });
        this.performanceStats.onBadGroup = () => {
            console.log("Enabling low fps mode");
            this.lowFpsMode = true;
            if (this.lowFpsTimeoutHandle !== null) {
                window.clearTimeout(this.lowFpsTimeoutHandle);
            }
            this.lowFpsTimeoutHandle = window.setTimeout(() => {
                this.lowFpsMode = false;
                console.log("Disabling low fps mode");
            }, 60 * 1000);
        };
    }
    get timeout() {
        return this.video ? 1000 / (this.lowFpsMode ? 15 : 30) : (this.stubFastdrawFrameCount ? 1000 / 30 : 1000 / 1);
    }
    get stream() {
        return this.mediaRecorder.stream;
    }
    get state() {
        return this.mediaRecorder.state;
    }
    set videoElement(v) {
        if (!this.canvasMode)
            return;
        this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
        if (v) {
            this.video = { img: v, rect: this.letterbox({ width: v.videoWidth, height: v.videoHeight }) };
            if (this.watermarkImg) {
                this.watermarkImg.rect = this.scaleWatermark(this.watermarkImg.img);
            }
        }
        else {
            this.video = null;
        }
        if (this.animationFrameRequestHandle !== null || this.timeoutHandle !== null) {
            if (!v) {
                this.stubFastdrawFrameCount = 7;
            }
            this.draw();
            this.setNextUpdateTimer();
        }
    }
    static getVideoBitrateForTrack(track) {
        if (track === undefined || track.kind !== "video")
            return 2600000;
        const { width, height } = track.getSettings();
        if (width <= 640 && height <= 480)
            return 2000000;
        if (width <= 1280 && height <= 800)
            return 3200000;
        if (width <= 1920 && height <= 1200)
            return 6000000;
        return 12000000;
    }
    start() {
        this.draw();
        this.setNextUpdateTimer();
        this.mediaRecorder.start(CanvasRecorder.mediaRecorderDataInterval);
    }
    stop() {
        this.mediaRecorder.stop();
        if (this.timeoutHandle !== null) {
            clearTimeout(this.timeoutHandle);
        }
        if (this.animationFrameRequestHandle !== null) {
            cancelAnimationFrame(this.animationFrameRequestHandle);
        }
        this.animationFrameRequestHandle = this.timeoutHandle = null;
    }
    getPerformanceCounter() {
        return this.performanceStats.getStats();
    }
    pause() {
        this.mediaRecorder.pause();
        this.performanceStats.pause();
    }
    resume() {
        this.mediaRecorder.resume();
    }
    blobResizeDecorator(event) {
        if (!this.ondataavailable) {
            return;
        }
        const minChunkSize = 256 * 1024;
        let blob = event.data;
        if (this.cachedBlob) {
            blob = new Blob([this.cachedBlob, blob], { type: blob.type });
            this.cachedBlob = null;
        }
        if (blob.size < minChunkSize) {
            this.cachedBlob = blob;
            return;
        }
        const bytesToSend = Math.floor(blob.size / minChunkSize) * minChunkSize;
        const blobToSend = blob.slice(0, bytesToSend);
        this.cachedBlob = blob.slice(bytesToSend);
        if (this.cachedBlob.size === 0) {
            this.cachedBlob = null;
        }
        const newEvent = new BlobEvent(event.type, { data: blobToSend, timecode: event.timecode });
        newEvent.isLast = false;
        this.ondataavailable(newEvent);
    }
    onStopDecorator(event) {
        var _a, _b;
        this.cachedBlob = this.cachedBlob ? new Blob([this.cachedBlob, CanvasRecorder.webmTail]) : CanvasRecorder.webmTail;
        const newEvent = new BlobEvent(this.cachedBlob.type, { data: this.cachedBlob });
        newEvent.isLast = true;
        (_a = this.ondataavailable) === null || _a === void 0 ? void 0 : _a.call(this, newEvent);
        this.cachedBlob = null;
        (_b = this.onstop) === null || _b === void 0 ? void 0 : _b.call(this, event);
    }
    drawImage(i) {
        this.ctx.drawImage(i.img, ...i.rect);
    }
    draw() {
        if (this.video) {
            this.drawImage(this.video);
            if (this.watermarkImg) {
                this.drawImage(this.watermarkImg);
            }
        }
        else {
            if (this.stubFastdrawFrameCount > 0) {
                this.stubFastdrawFrameCount--;
            }
            if (this.stubImg) {
                this.drawImage(this.stubImg);
            }
            else {
                this.ctx.fillStyle = "black";
                this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
            }
        }
    }
    shouldDraw() {
        if (this.mediaRecorder.state === "paused") {
            return false;
        }
        if (this.video === null) {
            return true;
        }
        const decodedFramesCount = this.video.img.webkitDecodedFrameCount;
        if (decodedFramesCount === undefined) {
            return true;
        }
        if (decodedFramesCount - this.decodedFramesCount > (this.lowFpsMode ? 1 : 0)) {
            this.decodedFramesCount = decodedFramesCount;
            return true;
        }
        return false;
    }
    frameUpdate(timestamp) {
        if (timestamp) {
            this.animationFrameRequestHandle = null;
        }
        else {
            this.timeoutHandle = null;
        }
        timestamp = timestamp !== null && timestamp !== void 0 ? timestamp : performance.now();
        if (this.lastCbTimestamp && timestamp) {
            const delta = timestamp - this.lastCbTimestamp;
            if (delta > 100) {
                console.info(`Too long since last frame update: ${delta}ms`);
                this.performanceStats.tickMissed(timestamp);
            }
        }
        this.lastCbTimestamp = timestamp;
        this.setNextUpdateTimer();
        if (this.shouldDraw()) {
            this.draw();
        }
    }
    setNextUpdateTimer() {
        if (!this.canvasMode)
            return;
        if (this.video && !document.hidden && CanvasRecorder.hasH264Support) {
            if (this.animationFrameRequestHandle === null) {
                this.animationFrameRequestHandle = requestAnimationFrame(this.frameUpdateCallback);
            }
        }
        else {
            if (this.timeoutHandle !== null) {
                window.clearTimeout(this.timeoutHandle);
            }
            this.timeoutHandle = window.setTimeout(this.frameUpdateCallback, this.timeout);
        }
    }
    letterbox(src) {
        const dstAspect = this.canvas.width / this.canvas.height;
        const srcAspect = src.width / src.height;
        const calc = (k0, k1) => {
            const scale = this.canvas[k0] / src[k0];
            const scaled = src[k1] * scale;
            const c0 = (this.canvas[k1] - scaled) / 2;
            return [Math.round(c0), Math.round(scaled)];
        };
        if (dstAspect >= srcAspect) {
            const [c0, c1] = calc("height", "width");
            return [c0, 0, c1, this.canvas.height];
        }
        else {
            const [c0, c1] = calc("width", "height");
            return [0, c0, this.canvas.width, c1];
        }
    }
    scaleWatermark(src) {
        if (!this.watermarkImg || !this.video) {
            return [0, 0, 0, 0];
        }
        const scale = this.video ? this.video.rect[2] / 1280 : 1;
        const wmWidth = Math.round(this.watermarkImg.img.width * scale);
        const wmHeight = Math.round(this.watermarkImg.img.height * scale);
        const videoRight = this.video.rect[0] + this.video.rect[2];
        const videoBottom = this.video.rect[1] + this.video.rect[3];
        const canvasScale = [this.canvas.width / 1280, this.canvas.height / 720];
        return [videoRight - wmWidth - Math.round(canvasScale[0] * 42),
            videoBottom - wmHeight - Math.round(canvasScale[0] * 46),
            wmWidth, wmHeight];
    }
}
CanvasRecorder.hasH264Support = MediaRecorder.isTypeSupported("video/webm;codecs=avc1.640034") || MediaRecorder.isTypeSupported("video/webm;codecs=h264");
CanvasRecorder.mediaRecorderDataInterval = CanvasRecorder.hasH264Support ? 4000 : 12000;
CanvasRecorder.webmTail = new Blob([
    Uint8Array.of(0x12, 0x54, 0xC3, 0x67, 0x01, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x28, 0xBF, 0x84, 0x84, 0xD9, 0xFF, 0xEC, 0x73, 0x73, 0x9F, 0x63, 0xC0, 0x84, 0x68, 0xCA, 0x81, 0x32, 0x67, 0xC8, 0x95, 0x45, 0xA3, 0x87, 0x45, 0x4E, 0x43, 0x4F, 0x44, 0x45, 0x52, 0x44, 0x87, 0x88, 0x5A, 0x45, 0x4E, 0x43, 0x41, 0x53, 0x54, 0x52)
], { type: "video/webm" });
(function (CanvasRecorder) {
    function loadImageBitmap(url, options) {
        return new Promise((resolve) => {
            const logoImage = new Image();
            logoImage.src = url;
            logoImage.onload = () => {
                createImageBitmap(logoImage, options)
                    .then(resolve)
                    .catch((e) => {
                    createImageBitmap(logoImage).then(resolve);
                });
                logoImage.onload = null;
            };
        });
    }
    CanvasRecorder.loadImageBitmap = loadImageBitmap;
    async function createStubImage(options) {
        const background = loadImageBitmap(options.backgroundUrl);
        const stubLogo = options.stubLogoUrl ? loadImageBitmap(options.stubLogoUrl, { premultiplyAlpha: "premultiply" }) : null;
        const canvas = document.createElement('canvas');
        canvas.width = options.width;
        canvas.height = options.height;
        const ctx = canvas.getContext("2d", { alpha: false, desynchronized: true });
        ctx.textAlign = "center";
        ctx.drawImage(await background, 0, 0, canvas.width, canvas.height);
        if (stubLogo) {
            const stubLogoImg = await stubLogo;
            const dx = Math.round((canvas.width - stubLogoImg.width) / 2);
            const dy = Math.round(canvas.height * (556 / 720));
            ctx.drawImage(stubLogoImg, dx, dy);
        }
        {
            ctx.fillStyle = "rgba(0, 0, 0, 0.6)";
            ctx.font = '24px Manrope';
            ctx.textBaseline = "top";
            const dx = Math.round(canvas.width / 2);
            const dy = Math.round(canvas.height * (427 / 720));
            ctx.fillText(options.userName, dx, dy);
        }
        {
            ctx.fillStyle = "#F2F1F9";
            ctx.font = '300px Manrope';
            ctx.textBaseline = "top";
            const dx = Math.round(canvas.width / 2);
            const dy = Math.round(canvas.height * (155 / 720));
            ctx.fillText(options.userName.charAt(0).toUpperCase(), dx, dy);
        }
        return createImageBitmap(canvas);
    }
    CanvasRecorder.createStubImage = createStubImage;
})(CanvasRecorder || (CanvasRecorder = {}));
//# sourceMappingURL=canvas_recorder.js.map